/**
 * RetentionPage — the Retention workspace (RET-050/051/053/054/055/057/058).
 *
 * The retention specialist's home:
 *   - Retention queue (GET /retention/queue) — alert cards grouped by trigger
 *     (coverage change / missed pay-stub / inactivity / COB) with severity +
 *     recommended action (RET-050/054).
 *   - Pay-stub reminders (GET /retention/paystub-reminders) with a Maya send
 *     action (POST /retention/paystub-reminders/{case}:send) (RET-051).
 *   - Lighter list views — follow-up sequences (RET-053), COB detections
 *     (RET-055), renewals (RET-057), and disenrollments (RET-058) — under
 *     secondary tabs.
 *
 * Shapes are verified against services/cases/retention.py. Every list degrades
 * gracefully when its endpoint isn't wired (friendly empty/disabled state).
 */
import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import {
  useRetention,
  type CobItem,
  type DisenrollmentItem,
  type FollowupSequence,
  type PaystubReminder,
  type RenewalItem,
  type RetentionQueueItem,
} from '../hooks/useRetention';
import { colors, radius, type PriorityTier } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { RoleImpactStrip } from '../components/PlatformImpactStrip';
import { ThreePanel } from '../components/ThreePanel';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityPill } from '../components/PriorityPill';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { EmptyState } from '../components/EmptyState';
import { AIInsightCard } from '../components/AIInsightCard';
import { useShellSlot } from '../components/shellContext';

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

const TRIGGER_LABEL: Record<string, string> = {
  coverage_change: 'Coverage change',
  missed_paystub: 'Missed pay-stub',
  inactivity: 'Inactivity',
  cob: 'Coordination of benefits',
};

function triggerLabel(trigger: string): string {
  return TRIGGER_LABEL[trigger] ?? trigger.replace(/_/g, ' ');
}

const TRIGGER_ICON: Record<string, string> = {
  coverage_change: '🔄',
  missed_paystub: '📄',
  inactivity: '💤',
  cob: '🧾',
};

/** Map the wire severity (low|med|high) to a priority tier for the pill. */
function severityTier(severity: string | null | undefined): PriorityTier {
  const s = (severity ?? '').toLowerCase();
  if (s === 'high') return 'critical';
  if (s === 'med' || s === 'medium') return 'high';
  if (s === 'low') return 'normal';
  return 'low';
}

type SecondaryTab = 'sequences' | 'cob' | 'renewals' | 'disenrollments';

const SECONDARY_TABS: { key: SecondaryTab; label: string }[] = [
  { key: 'sequences', label: 'Follow-up sequences' },
  { key: 'cob', label: 'COB detections' },
  { key: 'renewals', label: 'Renewals' },
  { key: 'disenrollments', label: 'Disenrollments' },
];

export function RetentionPage() {
  const {
    alerts,
    reminders,
    sequences,
    cob,
    renewals,
    disenrollments,
    alertsAvailable,
    remindersAvailable,
    sequencesAvailable,
    cobAvailable,
    renewalsAvailable,
    disenrollmentsAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    sendReminder,
  } = useRetention();
  const [secondary, setSecondary] = useState<SecondaryTab>('sequences');

  useShellSlot({ breadcrumb: 'Retention' });

  const highRisk = alerts.filter((a) => (a.severity ?? '').toLowerCase() === 'high').length;

  const reminderColumns: Column<PaystubReminder>[] = [
    {
      key: 'case',
      header: 'Case',
      render: (r) => (
        <Link to={`/cases/${encodeURIComponent(r.case_id)}`} style={link}>
          {r.case_id}
        </Link>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'deadline', header: 'Deadline', render: (r) => <span style={muted}>{r.deadline}</span> },
    {
      key: 'sent',
      header: 'Reminders sent',
      align: 'right',
      render: (r) => r.reminders_sent,
    },
    {
      key: 'action',
      header: 'Maya',
      render: (r) => (
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => sendReminder(r.case_id)}
          disabled={isWorking}
        >
          {r.reminders_sent > 0 ? 'Re-send' : 'Send reminder'}
        </button>
      ),
    },
  ];

  const sequenceColumns: Column<FollowupSequence>[] = [
    { key: 'id', header: 'Sequence', render: (s) => <span style={{ fontWeight: 600 }}>{s.id}</span> },
    {
      key: 'trigger',
      header: 'Trigger',
      render: (s) => <span style={muted}>{triggerLabel(s.trigger)}</span>,
    },
    {
      key: 'channels',
      header: 'Channels',
      render: (s) => <span style={muted}>{s.channel_mix.join(', ') || '—'}</span>,
    },
    {
      key: 'progress',
      header: 'Steps',
      render: (s) => {
        const done = s.steps.filter((st) => st.status === 'sent' || st.status === 'completed').length;
        return (
          <span style={muted}>
            {done} / {s.steps.length}
          </span>
        );
      },
    },
    { key: 'stop', header: 'Stop condition', render: (s) => <span style={muted}>{s.stop_condition}</span> },
  ];

  const cobColumns: Column<CobItem>[] = [
    {
      key: 'case',
      header: 'Case',
      render: (c) => (
        <Link to={`/cases/${encodeURIComponent(c.case_id)}`} style={link}>
          {c.case_id}
        </Link>
      ),
    },
    { key: 'evidence', header: 'Evidence', render: (c) => c.evidence },
    {
      key: 'severity',
      header: 'Severity',
      render: (c) => <PriorityPill tier={severityTier(c.severity)} />,
    },
  ];

  const renewalColumns: Column<RenewalItem>[] = [
    {
      key: 'case',
      header: 'Case',
      render: (r) => (
        <Link to={`/cases/${encodeURIComponent(r.case_id)}`} style={link}>
          {r.case_id}
        </Link>
      ),
    },
    { key: 'plan_year', header: 'Plan year', render: (r) => r.plan_year },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge value={r.status} /> },
  ];

  const disenrollmentColumns: Column<DisenrollmentItem>[] = [
    {
      key: 'case',
      header: 'Case',
      render: (d) => (
        <Link to={`/cases/${encodeURIComponent(d.case_id)}`} style={link}>
          {d.case_id}
        </Link>
      ),
    },
    { key: 'reason', header: 'Reason', render: (d) => d.reason },
    { key: 'status', header: 'Status', render: (d) => <StatusBadge value={d.status} /> },
  ];

  const secondaryBody = () => {
    if (secondary === 'sequences') {
      return !sequencesAvailable && sequences.length === 0 ? (
        <EmptyState
          title="Sequences not available yet"
          description="Automated retention follow-up sequences appear here once RET-053 is wired."
        />
      ) : (
        <DataTable
          columns={sequenceColumns}
          rows={sequences}
          rowKey={(s) => s.id}
          emptyTitle="No active sequences"
          emptyDescription="Multi-step retention follow-ups appear here as they are triggered."
        />
      );
    }
    if (secondary === 'cob') {
      return !cobAvailable && cob.length === 0 ? (
        <EmptyState
          title="COB detections not available yet"
          description="Coordination-of-benefits detections appear here once RET-055 is wired."
        />
      ) : (
        <DataTable
          columns={cobColumns}
          rows={cob}
          rowKey={(c) => c.case_id}
          emptyTitle="No COB detections"
          emptyDescription="Potential coordination-of-benefits situations appear here for review."
        />
      );
    }
    if (secondary === 'renewals') {
      return !renewalsAvailable && renewals.length === 0 ? (
        <EmptyState
          title="Renewals not available yet"
          description="Open-enrollment renewals appear here once RET-057 is wired."
        />
      ) : (
        <DataTable
          columns={renewalColumns}
          rows={renewals}
          rowKey={(r) => r.case_id}
          emptyTitle="No upcoming renewals"
          emptyDescription="Coverage renewals due for action appear here."
        />
      );
    }
    return !disenrollmentsAvailable && disenrollments.length === 0 ? (
      <EmptyState
        title="Disenrollments not available yet"
        description="Disenrollment records appear here once RET-058 is wired."
      />
    ) : (
      <DataTable
        columns={disenrollmentColumns}
        rows={disenrollments}
        rowKey={(d) => d.case_id}
        emptyTitle="No disenrollments"
        emptyDescription="Coverage lapses and disenrollments appear here for outreach."
      />
    );
  };

  return (
    <>
      <PageHeader
        title="Retention"
        subtitle="Continuous coverage monitoring, pay-stub reminders, and renewals (RET-050/051/053/054/055/057/058)."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      <RoleImpactStrip />

      {error && (
        <p style={errorText} role="alert">
          {error.message}
        </p>
      )}
      {actionError && (
        <p style={errorText} role="alert">
          {actionError.message}
        </p>
      )}

      <ThreePanel
        primary={
          <>
            {/* Retention queue — alert cards by trigger */}
            <Card
              title="Retention queue"
              actions={
                highRisk > 0 ? <StatusBadge status="danger" label={`${highRisk} high risk`} /> : undefined
              }
            >
              {isLoading ? (
                <LoadingIndicator text="Loading retention alerts…" />
              ) : !alertsAvailable && alerts.length === 0 ? (
                <EmptyState
                  title="Retention queue not available yet"
                  description="Coverage-change, missed-pay-stub, inactivity, and COB alerts appear here once the retention queue is wired (RET-050/054)."
                />
              ) : alerts.length === 0 ? (
                <EmptyState
                  title="No active retention alerts"
                  description="Triggered retention alerts appear here, prioritized by severity."
                />
              ) : (
                <div style={alertGrid}>
                  {alerts.map((a) => (
                    <AlertCard key={a.id} alert={a} />
                  ))}
                </div>
              )}
            </Card>

            {/* Pay-stub reminders (RET-051) */}
            <Card title="Pay-stub reminders" padded={false}>
              {isLoading ? (
                <div style={{ padding: 18 }}>
                  <LoadingIndicator text="Loading reminders…" />
                </div>
              ) : !remindersAvailable && reminders.length === 0 ? (
                <EmptyState
                  title="Reminders not available yet"
                  description="Maya pay-stub reminders appear here once RET-051 is wired."
                />
              ) : (
                <DataTable
                  columns={reminderColumns}
                  rows={reminders}
                  rowKey={(r) => r.case_id}
                  emptyTitle="No pending reminders"
                  emptyDescription="Patients due for a pay-stub reminder appear here for a one-tap Maya send."
                />
              )}
            </Card>

            {/* Secondary lists: sequences / cob / renewals / disenrollments */}
            <Card
              title="Lifecycle"
              padded={false}
              actions={
                <div style={tabBar} role="tablist" aria-label="Retention lifecycle views">
                  {SECONDARY_TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={secondary === t.key}
                      onClick={() => setSecondary(t.key)}
                      style={{ ...tabBtn, ...(secondary === t.key ? tabActive : {}) }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              }
            >
              {secondaryBody()}
            </Card>
          </>
        }
        rightContext={
          <>
            <AIInsightCard heading="Retention risk" confidence={0.78} acceptLabel="Queue outreach">
              {highRisk > 0 ? (
                <>
                  {highRisk} patient(s) show high retention risk (coverage change or missed pay-stub).
                  Recommend a Maya pay-stub reminder + a follow-up sequence today.
                </>
              ) : (
                <>
                  Coverage looks stable. Recommend a routine pay-stub reminder sweep to keep
                  documentation current and pre-empt inactivity alerts.
                </>
              )}
            </AIInsightCard>

            <div>
              <div style={summaryTitle}>Queue by trigger</div>
              <dl style={bucketList}>
                {(['coverage_change', 'missed_paystub', 'inactivity', 'cob'] as const).map((t) => {
                  const count = alerts.filter((a) => a.trigger === t).length;
                  return (
                    <div key={t} style={bucketRow}>
                      <dt style={bucketLabel}>
                        <span aria-hidden style={{ marginRight: 6 }}>
                          {TRIGGER_ICON[t]}
                        </span>
                        {triggerLabel(t)}
                      </dt>
                      <dd style={bucketValue}>{count}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          </>
        }
      />
    </>
  );
}

function AlertCard({ alert }: { alert: RetentionQueueItem }) {
  const tier = severityTier(alert.severity);
  return (
    <div style={alertCard}>
      <div style={alertHead}>
        <span style={alertTrigger}>
          <span aria-hidden style={{ marginRight: 6 }}>
            {TRIGGER_ICON[alert.trigger] ?? '⚑'}
          </span>
          {triggerLabel(alert.trigger)}
        </span>
        <PriorityPill tier={tier} />
      </div>
      <div style={alertCase}>
        <Link to={`/cases/${encodeURIComponent(alert.case_id)}`} style={link}>
          {alert.patient ?? alert.case_id}
        </Link>
      </div>
      {alert.recommended_action && (
        <div style={alertAction}>
          <span style={alertActionLabel}>Recommended</span> {alert.recommended_action}
        </div>
      )}
      <div style={alertMeta}>Detected {fmtDateTime(alert.detected_at)}</div>
    </div>
  );
}

const alertGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 12,
};
const alertCard: CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: 14,
  background: colors.white,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const alertHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};
const alertTrigger: CSSProperties = {
  fontSize: '0.74rem',
  fontWeight: 700,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
};
const alertCase: CSSProperties = { fontWeight: 600, fontSize: '0.95rem' };
const alertAction: CSSProperties = {
  fontSize: '0.84rem',
  color: colors.textPrimary,
  background: colors.surface,
  borderRadius: radius.sm,
  padding: '7px 9px',
};
const alertActionLabel: CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.action,
  marginRight: 4,
};
const alertMeta: CSSProperties = { fontSize: '0.74rem', color: colors.textMuted };
const tabBar: CSSProperties = { display: 'flex', gap: 4, flexWrap: 'wrap' };
const tabBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: colors.textSecondary,
  cursor: 'pointer',
};
const tabActive: CSSProperties = { color: colors.action, borderBottom: `2px solid ${colors.action}` };
const link: CSSProperties = { color: colors.action, fontWeight: 600 };
const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', marginBottom: 12 };
const summaryTitle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
  marginBottom: 8,
};
const bucketList: CSSProperties = { margin: 0, display: 'flex', flexDirection: 'column' };
const bucketRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '7px 0',
  borderBottom: `1px solid ${colors.surfaceAlt}`,
};
const bucketLabel: CSSProperties = { fontSize: '0.84rem', color: colors.textSecondary };
const bucketValue: CSSProperties = { margin: 0, fontSize: '0.92rem', fontWeight: 700, color: colors.textPrimary };
